'use client'

import {
	type ReactNode,
	createContext,
	useContext,
	useMemo,
	useState,
} from 'react'

import type { SizeGuideLine } from '../../types'

interface Line {
	text: SizeGuideLine['label']
	value: SizeGuideLine['id']
}

type LineId = Line['value']

export interface LinesContextType {
	lines: Line[]
	selectedLineId: LineId
	selectLineId: (newSelectedLineId: LineId) => void
}

interface LinesProviderProps {
	readonly children: React.ReactNode
	readonly lines: Line[]
}

export const LinesContext = createContext<LinesContextType | undefined>(
	undefined
)

export function LinesProvider({
	children,
	lines,
}: LinesProviderProps): ReactNode {
	const [selectedLineId, setSelectedLineId] = useState<string>(lines[0].value)

	const selectLineId = (lineId: string): void => {
		setSelectedLineId(lineId)
	}

	const linesProviderValue = useMemo(
		() => ({ lines, selectedLineId, selectLineId }),
		[lines, selectedLineId, setSelectedLineId]
	)

	return (
		<LinesContext.Provider value={linesProviderValue}>
			{children}
		</LinesContext.Provider>
	)
}

/** @throws {Error} If useLines is used without LinesProvider */
export const useLines = (): LinesContextType => {
	const context = useContext(LinesContext)
	if (!context) {
		throw new Error('useLines must be used within a LinesProvider')
	}
	return context
}
